import { buildFloorPlanSnapshot, createFloorObservation } from "./floorPlanViewModel";

const TERMINAL_RESERVATION_STATUSES = new Set(["CANCELLED", "REJECTED", "NO_SHOW"]);
const UPCOMING_RESERVATION_STATUSES = new Set(["PENDING", "CONFIRMED"]);

const normalizeStatus = (value) => String(value || "").trim().toUpperCase();

const dateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const reservationTimestamp = (reservation) => {
  const date = reservation.reservationDate || reservation.date;
  const time = reservation.startTime || reservation.time || "00:00";
  const timestamp = new Date(`${date}T${time}:00`).getTime();
  return Number.isNaN(timestamp) ? Number.POSITIVE_INFINITY : timestamp;
};

const reservationTableId = (reservation) =>
  String(reservation.tableId?._id || reservation.tableId || "");

const menuItemCount = (menu) =>
  (menu?.categories || []).reduce(
    (total, category) => total + (Array.isArray(category.items) ? category.items.length : 0),
    0
  );

const isReservationActiveAt = (reservation, currentMinutes) => {
  if (!reservation.startTime || !reservation.endTime) return false;
  const [startHour, startMinute] = reservation.startTime.split(":").map(Number);
  const [endHour, endMinute] = reservation.endTime.split(":").map(Number);
  const start = startHour * 60 + startMinute;
  const end = endHour * 60 + endMinute;
  return currentMinutes >= start && currentMinutes < end;
};

const currentTableBlockIds = (blocks, now) => {
  const currentTime = now.getTime();
  return new Set(
    blocks
      .filter((block) => {
        const start = new Date(block.startAt).getTime();
        const end = new Date(block.endAt).getTime();
        return Number.isFinite(start) && Number.isFinite(end) && start <= currentTime && currentTime < end;
      })
      .map((block) => String(block.tableId?._id || block.tableId || ""))
      .filter(Boolean)
  );
};

/**
 * @typedef {Object} RestaurantDashboardData
 * @property {{reservationCount: number, guestCount: number, pendingOrderCount: number, nextReservation: Object|null, tables: Object|null}} today
 * @property {{exists: boolean, isPublished: boolean, menuCount: number, menuId: string|null, title: string|null, itemCount: number, updatedAt: string|null, todayViews: number|null, weeklyViews: number|null}} menu
 * @property {{id: string|null, name: string|null, menuLink: string|null, subscription: Object|null}} restaurant
 * @property {{exists: boolean, elementCount: number}|null} floorPlan
 * @property {Array<Object>} attentionItems
 * @property {{upcomingReservations: Array<Object>, orders: Array<Object>}} recent
 * @property {{menuViews: boolean, unreadMessages: boolean, menus: boolean, reservations: boolean, orders: boolean, floorPlan: boolean, floorPlanPreview: boolean, tableAvailability: boolean}} capabilities
 */

/**
 * Converts existing restaurant resources into the stable, UI-facing dashboard shape.
 * Missing backend metrics intentionally remain null instead of receiving estimates.
 *
 * @returns {RestaurantDashboardData}
 */
export const buildDashboardViewModel = ({
  restaurant,
  restaurantAvailable = true,
  menus,
  reservations,
  tables,
  tableBlocks,
  floorPlan,
  reservationSettings,
  orders,
  ordersAvailable = true,
  subscription,
  now = new Date(),
}) => {
  const safeMenus = Array.isArray(menus) ? menus : [];
  const safeReservations = Array.isArray(reservations) ? reservations : [];
  const safeTables = Array.isArray(tables) ? tables : [];
  const safeBlocks = Array.isArray(tableBlocks) ? tableBlocks : [];
  const safeOrders = Array.isArray(orders) ? orders : [];
  const todayKey = dateKey(now);
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  const todayReservations = safeReservations.filter((reservation) => {
    const reservationDate = reservation.reservationDate || reservation.date;
    return reservationDate === todayKey && !TERMINAL_RESERVATION_STATUSES.has(normalizeStatus(reservation.status));
  });

  const upcomingReservations = safeReservations
    .filter((reservation) => {
      const status = normalizeStatus(reservation.status);
      return UPCOMING_RESERVATION_STATUSES.has(status) && reservationTimestamp(reservation) >= now.getTime();
    })
    .sort((first, second) => reservationTimestamp(first) - reservationTimestamp(second));

  const activeTables = safeTables.filter((table) => table.isActive !== false);
  const inactiveTables = safeTables.filter((table) => table.isActive === false);
  const activeTableIds = new Set(activeTables.map((table) => String(table._id)));
  const occupiedTableIds = new Set();
  const reservedTableIds = new Set();

  todayReservations.forEach((reservation) => {
    const tableId = reservationTableId(reservation);
    if (!tableId || !activeTableIds.has(tableId)) return;

    const status = normalizeStatus(reservation.status);
    if (status === "SEATED") occupiedTableIds.add(tableId);
    if (status === "CONFIRMED" && isReservationActiveAt(reservation, currentMinutes)) {
      reservedTableIds.add(tableId);
    }
  });

  occupiedTableIds.forEach((tableId) => reservedTableIds.delete(tableId));
  const blockedTableIds = new Set(
    [...currentTableBlockIds(safeBlocks, now)].filter((tableId) => activeTableIds.has(tableId))
  );
  occupiedTableIds.forEach((tableId) => blockedTableIds.delete(tableId));
  reservedTableIds.forEach((tableId) => blockedTableIds.delete(tableId));
  const unavailableActiveTableCount = new Set([
    ...occupiedTableIds,
    ...reservedTableIds,
    ...blockedTableIds,
  ]).size;

  const activeMenu = safeMenus.find((menu) => menu.isActive) || null;
  const primaryMenu = activeMenu || safeMenus[0] || null;
  const itemCount = menuItemCount(primaryMenu);
  const pendingOrders = safeOrders.filter((order) => normalizeStatus(order.status) === "PENDING");
  const unconfirmedReservations = safeReservations.filter(
    (reservation) => normalizeStatus(reservation.status) === "PENDING"
  );

  const recentOrders = [...safeOrders]
    .sort((first, second) => new Date(second.createdAt || 0) - new Date(first.createdAt || 0))
    .slice(0, 5);

  const todayUpcomingReservations = upcomingReservations.filter((reservation) => {
    const reservationDate = reservation.reservationDate || reservation.date;
    return reservationDate === todayKey;
  });

  const canBuildFloorPreview =
    Boolean(floorPlan) &&
    Array.isArray(floorPlan.elements) &&
    Array.isArray(tables) &&
    Array.isArray(reservations) &&
    Array.isArray(tableBlocks);
  const floorSnapshot = canBuildFloorPreview
    ? buildFloorPlanSnapshot({
        floorPlan: floorPlan.floorPlan,
        elements: floorPlan.elements,
        tables: safeTables,
        reservations: safeReservations,
        blocks: safeBlocks,
        observation: createFloorObservation({ now }),
      })
    : null;

  const restaurantSettings = restaurant?.restaurantSettings || {};
  const profileComplete = Boolean(
    restaurant?.name && restaurant?.phone && restaurantSettings.address
  );
  const openingHoursConfigured = Boolean(
    String(restaurantSettings.openingHours || "").trim() ||
    (Array.isArray(reservationSettings?.openingHours) && reservationSettings.openingHours.length)
  );
  const onboardingAvailable =
    restaurantAvailable &&
    Boolean(restaurant) &&
    Array.isArray(menus) &&
    Array.isArray(tables) &&
    floorPlan !== null &&
    floorPlan !== undefined &&
    reservationSettings !== null &&
    reservationSettings !== undefined;
  const onboardingSteps = [
    {
      id: "restaurant-profile",
      label: "تکمیل اطلاعات رستوران",
      completed: profileComplete,
      route: `/dashboard/settings/${restaurant?._id || restaurant?.id || ""}`,
    },
    {
      id: "create-menu",
      label: "ساخت منو",
      completed: Boolean(primaryMenu),
      route: primaryMenu?._id ? `/dashboard/edit-menu/${primaryMenu._id}` : "/dashboard/edit-menu",
    },
    {
      id: "add-menu-item",
      label: "افزودن آیتم منو",
      completed: itemCount > 0,
      route: primaryMenu?._id ? `/dashboard/edit-menu/${primaryMenu._id}` : "/dashboard/edit-menu",
    },
    {
      id: "add-tables",
      label: "افزودن میزها",
      completed: safeTables.length > 0,
      route: "/dashboard/reservations?section=SETTINGS",
    },
    {
      id: "design-floor",
      label: "طراحی سالن",
      completed: Boolean(floorPlan?.floorPlan) && Array.isArray(floorPlan?.elements) && floorPlan.elements.length > 0,
      route: "/dashboard/reservations?section=FLOOR_PLAN",
    },
    {
      id: "publish-menu",
      label: "انتشار منو",
      completed: Boolean(activeMenu),
      route: primaryMenu?._id ? `/dashboard/edit-menu/${primaryMenu._id}` : "/dashboard/edit-menu",
    },
    {
      id: "opening-hours",
      label: "تنظیم ساعات کاری",
      completed: openingHoursConfigured,
      route: "/dashboard/reservations?section=SETTINGS",
    },
  ];
  const completedOnboardingSteps = onboardingSteps.filter((step) => step.completed).length;

  const attentionItems = [];
  if (unconfirmedReservations.length) {
    attentionItems.push({
      id: "unconfirmed-reservations",
      type: "reservation",
      severity: "warning",
      label: "رزرو تأییدنشده",
      count: unconfirmedReservations.length,
      route: "/dashboard/reservations?filter=PENDING",
    });
  }
  if (pendingOrders.length) {
    attentionItems.push({
      id: "new-orders",
      type: "order",
      severity: "info",
      label: "سفارش در انتظار",
      count: pendingOrders.length,
      route: "/dashboard/orders",
    });
  }
  if (inactiveTables.length) {
    attentionItems.push({
      id: "out-of-service-tables",
      type: "table",
      severity: "warning",
      label: "میز خارج از سرویس",
      count: inactiveTables.length,
      route: "/dashboard/reservations?section=SETTINGS",
    });
  }
  if (Array.isArray(menus) && (!primaryMenu || !activeMenu || itemCount === 0)) {
    attentionItems.push({
      id: "incomplete-menu",
      type: "menu",
      severity: "warning",
      label: !primaryMenu ? "منویی ساخته نشده است" : !activeMenu ? "منوی منتشرشده ندارید" : "منوی فعال خالی است",
      count: null,
      route: primaryMenu?._id ? `/dashboard/edit-menu/${primaryMenu._id}` : "/dashboard/edit-menu",
    });
  }

  return {
    today: {
      reservationCount: todayReservations.length,
      guestCount: todayReservations.reduce(
        (total, reservation) => total + (Number(reservation.partySize) || 0),
        0
      ),
      pendingOrderCount: pendingOrders.length,
      nextReservation: upcomingReservations[0] || null,
      tables: Array.isArray(tables)
        ? {
            total: safeTables.length,
            occupied: occupiedTableIds.size,
            reserved: reservedTableIds.size,
            blocked: blockedTableIds.size,
            available: Math.max(0, activeTables.length - unavailableActiveTableCount),
            outOfService: inactiveTables.length,
          }
        : null,
    },
    menu: {
      exists: Boolean(primaryMenu),
      isPublished: Boolean(activeMenu),
      menuCount: safeMenus.length,
      menuId: primaryMenu?._id || null,
      title: primaryMenu?.title || null,
      itemCount,
      updatedAt: primaryMenu?.updatedAt || null,
      todayViews: null,
      weeklyViews: null,
    },
    restaurant: {
      id: restaurant?._id || restaurant?.id || null,
      name: restaurant?.name || null,
      menuLink: restaurant?.menuLink || null,
      subscription: subscription
        ? {
            plan: subscription.plan || null,
            status: subscription.status || null,
            billingCycle: subscription.billingCycle || null,
            isExpired: Boolean(subscription.isExpired),
            expiresAt: subscription.endDate || null,
            daysRemaining: subscription.daysRemaining ?? null,
          }
        : null,
    },
    floorPlan: floorPlan
      ? {
          exists: Boolean(floorPlan.floorPlan) && Array.isArray(floorPlan.elements) && floorPlan.elements.length > 0,
          elementCount: Array.isArray(floorPlan.elements) ? floorPlan.elements.length : 0,
          preview: floorSnapshot
            ? {
                plan: floorSnapshot.displayMap.plan,
                elements: floorSnapshot.displayMap.elements,
                tables: safeTables,
                states: floorSnapshot.states,
              }
            : null,
        }
      : null,
    attentionItems,
    onboarding: {
      available: onboardingAvailable,
      complete: onboardingAvailable && completedOnboardingSteps === onboardingSteps.length,
      completedCount: completedOnboardingSteps,
      totalCount: onboardingSteps.length,
      steps: onboardingSteps,
    },
    recent: {
      upcomingReservations: (
        todayUpcomingReservations.length ? todayUpcomingReservations : upcomingReservations
      ).slice(0, 5),
      orders: recentOrders,
    },
    capabilities: {
      menuViews: false,
      unreadMessages: false,
      menus: Array.isArray(menus),
      reservations: Array.isArray(reservations),
      orders: ordersAvailable && Array.isArray(orders),
      floorPlan: floorPlan !== null && floorPlan !== undefined,
      floorPlanPreview: Boolean(floorSnapshot),
      tableAvailability:
        Array.isArray(tables) && Array.isArray(reservations) && Array.isArray(tableBlocks),
    },
    unreadMessages: null,
  };
};
